/** The design system's source of truth.
 *
 * Shared by Ant Design (`theme/tokens.ts`), the generated CSS custom properties
 * (`styles/design-tokens.css`, via `bun run design:generate`), and any style that
 * needs a numeric value. Prefer Tailwind utilities in JSX; use these tokens for
 * Ant's style/size APIs. Editing a value here is the ONLY way to change it —
 * `design:check` fails if the generated CSS is stale, and `check-design-system.ts`
 * rejects raw hex, spacing, font-size and radius literals in components.
 *
 * ── Direction ───────────────────────────────────────────────────────────────
 * Derived from Airbnb's system: a warm consumer-marketplace look on a pure white
 * canvas, carried by ONE brand voltage — Rausch (`primary`, #ff385c). The rules
 * that keep it coherent, in rough order of how easy they are to break:
 *
 *  - One accent, used scarcely. Rausch is for primary CTAs and save/active
 *    states only. A page should read ~90% white and ink with one or two Rausch
 *    moments. `plus` and `luxe` are sub-brand accents — not general-purpose.
 *  - Text on red uses `primary-deep`, never `primary`. White on Rausch is
 *    3.5:1 and fails WCAG AA at button sizes; white on `primary-deep` is 4.9:1.
 *    So primary buttons, links and count badges are `primary-deep` (pressed:
 *    `primary-press`), while Rausch stays for marks that carry no text:
 *    switches, focus-free indicators, unread dots, the AI's colour.
 *  - Ink, never black. `ink` (#222222) is the dominant text tone; `charcoal`
 *    for long-form running copy, `ash` for sub-labels, `mute` for disabled.
 *    Star ratings render in ink too — gold stars read cheap here.
 *  - One elevation tier. `--shadow-2` is the only shadow in the system (hover-
 *    floated cards, dropdowns, the search surface). Depth otherwise comes from
 *    rounded-corner clipping and white-on-white surface separation, not layers.
 *  - Soft everywhere. `control` 8px, `card` 14px, `lg`/`xl` for large surfaces,
 *    `pill` for search bars and circular controls. The body grid is the only
 *    hard corner.
 *  - Focus is a 2px ink border, not a glow or a coloured ring (`--focus-ring`).
 *  - Modest display weights. Headlines sit at 500-700, not heavier; the layout
 *    is meant to lean on imagery and whitespace rather than typographic muscle.
 *    `fontSize.rating` (64px, the `.t-rating` role) is the single deliberate
 *    exception — the one place type alone carries the hierarchy.
 *
 * Adapted, not copied: Airbnb sizes for a consumer page with a handful of
 * controls, and this is a merchant dashboard. `--section-gap` stays compact
 * (24px) rather than the source system's 64px band rhythm, and the marketplace
 * furniture (search orb, property cards, reservation rail, date-picker cells)
 * has no counterpart here and is deliberately absent.
 */
export const colors = {
  canvas: "#ffffff",
  "soft-cloud": "#f7f7f7",
  "surface-strong": "#f2f2f2",
  hairline: "#dddddd",
  "border-light": "#ebebeb",
  ink: "#222222",
  charcoal: "#3f3f3f",
  ash: "#6a6a6a",
  mute: "#929292",
  stone: "#c1c1c1",
  primary: "#ff385c",
  "primary-deep": "#e00b41",
  "primary-press": "#bf0937",
  "primary-disabled": "#ffd1da",
  "primary-light": "#fff1ec",
  "primary-bg": "#fff8f5",
  "primary-border": "#ffd6dd",
  plus: "#92174d",
  luxe: "#460479",
  info: "#428bff",
  "info-bg": "#eef4f8",
  "info-border": "#b8d6f0",
  error: "#c13515",
  "error-deep": "#b32505",
  "error-bg": "#fdeae3",
  "error-border": "#f3d4cc",
  success: "#008a05",
  "success-text": "#006504",
  "success-bg": "#dff2e3",
  "success-border": "#c8e6cf",
  warning: "#b45309",
  "warning-bg": "#fef3c7",
  "warning-border": "#fcd34d",
  fb: "#1877f2",
  line: "#06c755",
  instagram: "#e4405f",
  tiktok: "#000000",
  // Modal backdrop base; opacity (50%) is applied at render time.
  scrim: "#000000",
} as const;

export const spacingUnit = 4;
export const space = {
  0: 0,
  0.5: spacingUnit / 2,
  1: spacingUnit,
  1.5: spacingUnit * 1.5,
  2: spacingUnit * 2,
  2.5: spacingUnit * 2.5,
  3: spacingUnit * 3,
  3.5: spacingUnit * 3.5,
  4: spacingUnit * 4,
  5: spacingUnit * 5,
  6: spacingUnit * 6,
  7: spacingUnit * 7,
  8: spacingUnit * 8,
  9: spacingUnit * 9,
  10: spacingUnit * 10,
  12: spacingUnit * 12,
  14: spacingUnit * 14,
  16: spacingUnit * 16,
  20: spacingUnit * 20,
  24: spacingUnit * 24,
} as const;

export const fontSize = {
  tag: 8,
  badge: 11,
  caption: 12,
  body: 14,
  bodyLarge: 16,
  subheading: 18,
  heading: 20,
  title: 24,
  hero: 28,
  displaySmall: 36,
  display: 48,
  price: 56,
  rating: 64,
  priceLarge: 72,
} as const;

export const radius = { chip: 4, small: 6, control: 8, card: 14, lg: 20, xl: 32, pill: 999 } as const;
/** Control ladder. `small` (32) is the spec's circular icon-button and `large`
 * (48) its primary CTA / minimum touch target; `normal` (40) is the dashboard's
 * own compact step, which the source system has no equivalent for because it
 * has no dense surfaces. Buttons and fields MUST share a height — they sit in
 * the same row here, so the source system's 56px stacked text-input does not
 * transfer (review/tests/interactions.spec.ts locks this). */
export const control = { small: space[8], normal: space[10], large: space[12], icon: space[9] } as const;
export const layout = { page: 1240, detail: 1024, form: 768, auth: 400 } as const;
export const fontFamily =
  '"Inter", "Noto Sans Thai", -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, Roboto, "Helvetica Neue", sans-serif';
/** Brand display face: landing headings, app page titles and large figures
 * (prices, dashboard numbers). Bai Jamjuree carries Thai and Latin in one
 * squared, technical design, so the two scripts share a voice. Never use it
 * for labels, buttons, table data or body copy. */
export const displayFontFamily = '"Bai Jamjuree", "Noto Sans Thai", sans-serif';

export const semanticColors = {
  text: "ink",
  "text-secondary": "charcoal",
  "text-muted": "ash",
  "text-inactive": "mute",
  "text-dark": "ink",
  "text-sub": "charcoal",
  "chat-accent": "ink",
  surface: "canvas",
  "surface-secondary": "soft-cloud",
  "surface-layout": "soft-cloud",
  "surface-sidebar": "soft-cloud",
  "surface-auth": "soft-cloud",
  "surface-inactive": "soft-cloud",
  border: "hairline",
  "border-layout": "hairline",
  "border-card": "hairline",
  "border-auth": "hairline",
} satisfies Record<string, keyof typeof colors>;
